import { LoadingIcon } from 'morpheus-ui';
import React, { ComponentProps } from 'react';
import { VariantProps } from 'tailwind-variants';
import { Icon, iIconProps } from '../Icon';
import {
  InputLoadingIcon,
  errorTextClasses,
  helperTextClasses,
  inputClasses,
  inputGroupClasses,
  inputLeftIconClasses,
  inputRightIconClasses,
  labelClasses,
} from './variants';

type InputProps = ComponentProps<'input'> &
  VariantProps<typeof inputGroupClasses> & {
    IconRight?: iIconProps;
    IconLeft?: iIconProps;
    label?: string;
    helperText?: string;
    error?: boolean;
    errorMessage?: string | any;
    mask?: string;
  };

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'md',
      label,
      helperText,
      IconLeft,
      IconRight,
      loading,
      error = false,
      errorMessage,
      disabled,
      className,
      mask,
      ...props
    },
    ref
  ) => {
    const hasIconLeft = !!IconLeft || loading;
    const hasIconRight = !!IconRight;
    const hasErrorMessage = !!errorMessage;
    const hasError = error || hasErrorMessage;

    return (
      <div className={inputGroupClasses({ variant, className })}>
        {IconLeft && !loading && (
          <Icon
            Icon={IconLeft.Icon}
            type={IconLeft.type}
            className={`${inputLeftIconClasses({ disabled, variant })} ${
              IconLeft.className
            }`}
            onClick={!disabled ? IconLeft.onClick : undefined}
          />
        )}
        {IconRight && (
          <Icon
            Icon={IconRight.Icon}
            type={IconRight.type}
            className={`${inputRightIconClasses({ disabled, variant })} ${
              IconRight.className
            }`}
            onClick={!disabled ? IconRight.onClick : undefined}
          />
        )}

        {loading && (
          <Icon
            Icon={LoadingIcon}
            type='svg'
            className={InputLoadingIcon({ disabled, variant })}
          />
        )}

        <input
          {...props}
          placeholder={props.placeholder || ' '}
          ref={ref}
          disabled={disabled}
          className={inputClasses({
            variant,
            hasIconLeft,
            hasIconRight,
            disabled,
            hasError,
          })}
          onChange={(e) => {
            if (mask) {
              const value = applyMask(e.target.value, mask);
              e.target.value = value;
            }

            props.onChange && props.onChange(e);
          }}
        />

        {label && (
          <label
            htmlFor={props.id}
            className={labelClasses({ variant, hasIconLeft, disabled })}
          >
            {label}
          </label>
        )}

        {helperText && !hasErrorMessage && (
          <span className={helperTextClasses({ disabled })}>{helperText}</span>
        )}

        {hasErrorMessage && (
          <span className={errorTextClasses({ disabled })}>{errorMessage}</span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };

// (99) 9 9999-9999

function applyMask(text: string, mask: string): string {
  let formattedText = '';

  let maskIndex = 0;
  for (let i = 0; i < text.length; i++) {
    if (formattedText.length >= mask.length) {
      break;
    }

    // Separadores digitados ou que ja estavam no texto sao ignorados: a mascara
    // so os coloca antes do proximo caractere valido. Assim o backspace apaga o
    // separador do fim em vez de ele voltar na hora.
    if (!/[a-zA-Z0-9]/.test(text[i])) {
      continue;
    }

    let maskChar = mask[maskIndex];
    while (maskChar !== undefined && maskChar !== '#' && maskChar !== '9') {
      formattedText += maskChar;
      maskIndex++;

      maskChar = mask[maskIndex];
    }

    if (maskChar === '#' && /[a-zA-Z]/.test(text[i])) {
      formattedText += text[i] || '';
      maskIndex++;
    } else if (maskChar === '9' && /\d/.test(text[i])) {
      formattedText += text[i] || '';
      maskIndex++;
    }
  }

  return formattedText;
}
